<template>
  <article
    :class="[$style.config, $style.container]"
    @click="(e) => $handleBlockLinkClick(e, cardLinkEl?.$el)"
  >
    <AspectRatio
      v-if="data?.fields?.featuredImage"
      :class="$style.thumbnailWrap"
      :ratio="`283x194`"
    >
      <BasePicture
        :src="
          data?.fields?.featuredImage?.fields?.file?.url +
          `?w=283&h=194&fit=thumb`
        "
        :alt="data?.fields?.featuredImage?.fields?.description || ''"
        :img-class="$style.img"
      />
    </AspectRatio>
    <div v-else :class="$style.emptyThumbnail">
      <AspectRatio :class="$style.thumbnailWrap" ratio="283x194" />
    </div>
    <div :class="$style.content">
      <div :class="$style.meta">
        <span
          v-if="!data?.fields?.hidePublishedDateTime"
          :class="$style.date"
          >{{ computedLocalPublishedDateTime }}</span
        >
        <template v-if="data?.fields?.tags?.length">
          <span
            v-if="
              !data?.fields?.hidePublishedDateTime &&
              computedLocalPublishedDateTime
            "
          >
            —
          </span>
          <span v-for="(t, i) in data?.fields?.tags" :key="t?.sys?.id">
            {{ t?.fields?.label }}
            <template v-if="i !== data?.fields?.tags?.length - 1"
              >,{{ ' ' }}</template
            >
          </span>
        </template>
      </div>

      <BaseHeading size="h4" :class="$style.title">
        <NuxtLinkLocale
          ref="cardLink"
          :to="`/news/${data?.fields?.path}`"
          :class="$style.titleLink"
          target="_blank"
        >
          {{ data?.fields?.title }}
        </NuxtLinkLocale>
      </BaseHeading>

      <MarkedParser :md="data?.fields?.excerpt" :class="$style.copy" />
    </div>
  </article>
</template>

<script setup lang="ts">
import { formatLocalizedDate } from '../utils/localizeDates'

import type { TypeArticleWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { locale } = useI18n()
const { $handleBlockLinkClick } = useNuxtApp()

const cardLinkEl = useTemplateRef<ComponentPublicInstance | null>('cardLink')

const props = defineProps<{
  data: TypeArticleWithoutUnresolvableLinksResponse | undefined
}>()

const computedLocalPublishedDateTime = computed(() => {
  const publishedDateTime = props?.data?.fields?.publishedDateTime || ''
  return formatLocalizedDate(publishedDateTime, 'PP', locale.value)
})
</script>

<style lang="scss" module>
.config {
  --padding--base: 16px;
  --padding--lg: 10px 44px;
}

.container {
  @include fluid-type(21px, 23px);
  cursor: pointer;
  background: #fff;
  border: 1px solid var(--gray-4);
  box-shadow: 0 4px 13px rgba(#000, 0.06);
  display: grid;
  align-items: center;
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;

  @include media('xs') {
    grid-template-columns: 283px 1fr;
    text-align: left;
  }

  &:hover,
  &:focus {
    border: 1px solid var(--gray-5);
    box-shadow: 0 4px 13px rgba(#000, 0.12);
  }
}

.thumbnailWrap {
  align-self: flex-start;
}

.emptyThumbnail {
  height: 0;

  @include media('xs') {
    height: unset;
  }
}

.content {
  padding: var(--padding--base);

  @include media('sm') {
    padding: var(--padding--lg);
  }
}

.title {
  margin-bottom: 11px;
}

.titleLink {
  color: inherit;
  text-decoration: none;
}

.meta {
  font-size: 14px;
  line-height: math.div(22px, 14px);
  margin-bottom: 3px;

  a {
    color: var(--purple);
  }
}

.date {
  color: #828282;
  font-family: var(--base-font-family);
  font-size: to-rem(14px);
  line-height: math.div(22, 14);
  text-transform: uppercase;
}

.copy {
  color: var(--gray-8);
  margin-top: 5px;
  font-size: to-rem(17px);
  line-height: math.div(27, 17);

  word-break: break-word;
  text-overflow: ellipsis;
  line-clamp: 2;
  -webkit-box-orient: vertical;

  display: -webkit-box;
  height: calc(to-rem(17px) * math.div(27, 17) * 2);
  overflow: hidden;
}

.cta {
  margin-top: 17px;
}
</style>
